import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useMap } from "@vis.gl/react-google-maps";
import { fetchLabels } from "../../../../utils/api";
import { mapChromeShell } from "../../../../styles/mapChromeShell";

const sortLabels = (data) => {
    const noneLabel = data.find((label) => label.name === "ラベルなし");
    const restLabels = data.filter((label) => label.name !== "ラベルなし");
    return noneLabel ? [noneLabel, ...restLabels] : restLabels;
};

/**
 * externalLabels の意味:
 *   null      = 親がまだロード中 → 自前フェッチもせず loading=true を返す
 *   []        = 親がロード済みだがラベルなし → そのまま使用
 *   配列      = 親がロード済み → そのまま使用
 *   未指定    = 親が関与しない（スタンドアロン用途）→ 自前フェッチ
 */
const useLabelData = (projectId, isReadOnly, publicUrl, externalLabels = undefined) => {
    const [labels, setLabels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // null = 親がロード中。自前フェッチせず待機
        if (externalLabels === null) {
            setLoading(true);
            return;
        }

        // 配列が渡されている = 親から受け取った値を使う
        if (Array.isArray(externalLabels)) {
            setLabels(sortLabels(externalLabels));
            setLoading(false);
            return;
        }

        // 公開ページは外部ラベルのみ使用（自前フェッチなし）
        if (publicUrl) {
            setLabels([]);
            setLoading(false);
            return;
        }

        // externalLabels === undefined かつ非公開モード → 自前フェッチ
        const loadLabels = async () => {
            if (!projectId) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const data = await fetchLabels(projectId);
                setLabels(sortLabels(data));
            } catch (err) {
                console.error("Failed to fetch labels:", err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        loadLabels();
    }, [projectId, isReadOnly, publicUrl, externalLabels]);

    return { labels, loading, error };
};

const useSelectionState = (labels, initialValue, onChange) => {
    // すべて選択されているかどうかの判定（リアルタイムで計算）
    const allSelected = useMemo(() => {
        return labels.length > 0 && initialValue.length === labels.length;
    }, [labels.length, initialValue.length]);

    const toggleAll = useCallback(() => {
        if (allSelected) {
            onChange([]);
        } else {
            onChange(labels.map((label) => label.id));
        }
    }, [allSelected, labels, onChange]);

    const toggleOne = useCallback((id) => {
        const newSelection = initialValue.includes(id)
            ? initialValue.filter((value) => value !== id)
            : [...initialValue, id];
        onChange(newSelection);
    }, [initialValue, onChange]);

    return { allSelected, toggleAll, toggleOne };
};

function MobileHamburgerMenu({
    projectId,
    filteredLabelIds,
    onFilterChange,
    showGeoJson,
    onToggleGeoJson,
    isReadOnly = false,
    publicUrl = null,
    useKml = false,
    labels: propLabels, // 外部から渡されるラベルデータ
    kmlTitle = "KMLファイル" // KMLタイトル
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('filter'); // 'filter' or 'layer'
    const [searchQuery, setSearchQuery] = useState(''); // 検索クエリ
    const [swipeStartY, setSwipeStartY] = useState(null);
    const [panelTransform, setPanelTransform] = useState(0);
    const map = useMap();
    const controlRef = useRef(null);
    const isInitialized = useRef(false);
    const panelRef = useRef(null);

    const { labels, loading, error } = useLabelData(projectId, isReadOnly, publicUrl, propLabels);
    const { allSelected, toggleAll, toggleOne } = useSelectionState(labels, filteredLabelIds, onFilterChange);

    // Google Mapsのコントロールコンテナを作成
    useEffect(() => {
        if (!map || !window.google || isInitialized.current) return;

        const controlDiv = document.createElement("div");
        controlDiv.className = mapChromeShell.hamburgerControlContainer;
        controlRef.current = controlDiv;

        // 地図の左上にコントロールを追加
        map.controls[window.google.maps.ControlPosition.TOP_LEFT].push(controlDiv);
        isInitialized.current = true;

        return () => {
            const index = map.controls[window.google.maps.ControlPosition.TOP_LEFT].getArray().indexOf(controlDiv);
            if (index > -1) {
                map.controls[window.google.maps.ControlPosition.TOP_LEFT].removeAt(index);
            }
            isInitialized.current = false;
        };
    }, [map]);

    const handleToggle = () => {
        setIsOpen(!isOpen);
        setSearchQuery(''); // メニューを開く際に検索をリセット
        setPanelTransform(0); // パネルの位置をリセット
    };

    const handleClose = () => {
        setIsOpen(false);
        setSearchQuery('');
        setPanelTransform(0);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSearchQuery(''); // タブ変更時に検索をリセット
    };

    // バックドロップクリックで閉じる
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    // スワイプジェスチャーの処理
    const handleTouchStart = (e) => {
        const touch = e.touches[0];
        setSwipeStartY(touch.clientY);
    };

    const handleTouchMove = (e) => {
        if (swipeStartY === null) return;
        const touch = e.touches[0];
        const deltaY = touch.clientY - swipeStartY;
        
        // 下方向へのスワイプのみ許可
        if (deltaY > 0) {
            setPanelTransform(deltaY);
        }
    };

    const handleTouchEnd = () => {
        // 一定距離以上スワイプしたら閉じる
        if (panelTransform > 100) {
            handleClose();
        } else {
            setPanelTransform(0);
        }
        setSwipeStartY(null);
    };

    // 検索でフィルタリングされたラベル
    const filteredLabels = useMemo(() => {
        if (!searchQuery.trim()) {
            return labels;
        }
        const query = searchQuery.toLowerCase();
        return labels.filter(label => 
            label.name.toLowerCase().includes(query)
        );
    }, [labels, searchQuery]);

    if (!controlRef.current) {
        return null;
    }

    return createPortal(
        <div className={mapChromeShell.hamburgerMenu}>
            {/* ハンバーガーボタン */}
            <button
                className={`${mapChromeShell.hamburgerButton} ${isOpen ? mapChromeShell.hamburgerButtonActive : ""}`}
                onClick={handleToggle}
                aria-label="メニューを開く"
            >
                <span
                    className={
                        isOpen
                            ? `${mapChromeShell.hamburgerButtonIcon} ${mapChromeShell.hamburgerButtonIconActive}`
                            : mapChromeShell.hamburgerButtonIcon
                    }
                >
                    dehaze
                </span>
                <p
                    className={
                        isOpen
                            ? `${mapChromeShell.hamburgerButtonLabel} ${mapChromeShell.hamburgerButtonLabelActive}`
                            : mapChromeShell.hamburgerButtonLabel
                    }
                >
                    設定
                </p>
                {/* 全て=全部表示 / 部分選択=件数 / —=0件選択。メニュー開中は非表示 */}
                {!isOpen && labels.length > 0 && (
                    <span
                        className={`${mapChromeShell.hamburgerBadge} ${
                            allSelected
                                ? mapChromeShell.hamburgerBadgeNeutral
                                : mapChromeShell.hamburgerBadgeActive
                        }`}
                    >
                        {allSelected
                            ? "全て"
                            : filteredLabelIds.length === 0
                              ? "—"
                              : filteredLabelIds.length}
                    </span>
                )}
            </button>

            {/* バックドロップ（オーバーレイ） */}
            {isOpen && (
                <div 
                    className={mapChromeShell.hamburgerBackdrop}
                    onClick={handleBackdropClick}
                />
            )}

            {/* メニューパネル */}
            {isOpen && (
                <div 
                    className={mapChromeShell.hamburgerPanel}
                    ref={panelRef}
                    style={{ transform: `translateY(${panelTransform}px)` }}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <div className={mapChromeShell.hamburgerHeader}>
                        {/* ドラッグハンドル */}
                        <div className={mapChromeShell.hamburgerDragHandle} />
                        <h3 className={mapChromeShell.hamburgerTitle}>マップ設定</h3>
                        <button
                            className={mapChromeShell.hamburgerClose}
                            onClick={handleClose}
                            aria-label="メニューを閉じる"
                        >
                            <span className={mapChromeShell.hamburgerCloseIcon}>close</span>
                        </button>
                    </div>

                    {/* タブナビゲーション - KML設定がONの場合のみレイヤータブを表示 */}
                    <div className={mapChromeShell.hamburgerTabs}>
                        <button
                            className={`${mapChromeShell.hamburgerTab} ${activeTab === "filter" ? mapChromeShell.hamburgerTabActive : ""}`}
                            onClick={() => handleTabChange('filter')}
                        >
                            フィルター
                        </button>
                        {useKml && (
                            <button
                                className={`${mapChromeShell.hamburgerTab} ${activeTab === "layer" ? mapChromeShell.hamburgerTabActive : ""}`}
                                onClick={() => handleTabChange('layer')}
                            >
                                レイヤー
                            </button>
                        )}
                    </div>

                    {/* タブコンテンツ */}
                    <div className={mapChromeShell.hamburgerContent}>
                        {activeTab === 'filter' && (
                            <div className={mapChromeShell.filterContent}>
                                {loading ? (
                                    <div className={mapChromeShell.mobileLoading}>読み込み中...</div>
                                ) : error ? (
                                    <div className={mapChromeShell.mobileError}>エラーが発生しました</div>
                                ) : (
                                    <>
                                        {/* 検索バー */}
                                        <div className={mapChromeShell.filterSearch}>
                                            <span className={mapChromeShell.filterSearchIcon}>
                                                search
                                            </span>
                                            <input
                                                type="text"
                                                className={mapChromeShell.filterSearchInput}
                                                placeholder="ラベルを検索..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                            />
                                            {searchQuery && (
                                                <button
                                                    className={mapChromeShell.filterSearchClear}
                                                    onClick={() => setSearchQuery('')}
                                                    aria-label="検索をクリア"
                                                >
                                                    <span className={mapChromeShell.filterSearchClearIcon}>close</span>
                                                </button>
                                            )}
                                        </div>

                                        <div className={mapChromeShell.filterHeader}>
                                            <button
                                                className={mapChromeShell.filterToggleAll}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    toggleAll();
                                                }}
                                            >
                                                {allSelected ? 'すべて解除' : 'すべて選択'}
                                            </button>
                                            <span className={mapChromeShell.filterCount}>
                                                {filteredLabelIds.length} / {labels.length}
                                            </span>
                                        </div>
                                        <div className={mapChromeShell.filterList}>
                                            {filteredLabels.length === 0 ? (
                                                <div className={mapChromeShell.filterEmpty}>
                                                    {searchQuery ? '検索結果が見つかりませんでした' : 'ラベルがありません'}
                                                </div>
                                            ) : (
                                                filteredLabels.map((label) => (
                                                    <label
                                                        key={label.id}
                                                        className={mapChromeShell.filterItem}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            className={mapChromeShell.filterCheckbox}
                                                            checked={filteredLabelIds.includes(label.id)}
                                                            onChange={() => toggleOne(label.id)}
                                                        />
                                                        <span
                                                            className={mapChromeShell.filterColor}
                                                            style={{ backgroundColor: label.color }}
                                                        ></span>
                                                        <span className={mapChromeShell.filterName}>
                                                            {label.name}
                                                        </span>
                                                    </label>
                                                ))
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {activeTab === 'layer' && useKml && (
                            <div className={mapChromeShell.layerContent}>
                                <div className={mapChromeShell.layerItem}>
                                    <label className={mapChromeShell.layerToggleRow}>
                                        <input
                                            type="checkbox"
                                            className={mapChromeShell.layerCheckbox}
                                            checked={showGeoJson}
                                            onChange={() => onToggleGeoJson(!showGeoJson)}
                                        />
                                        <span className={mapChromeShell.layerName}>
                                            {kmlTitle}
                                        </span>
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>,
        controlRef.current
    );
}

export default MobileHamburgerMenu;
